import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dataset } from '@/lib/types';
import { Equals, Target, TrendDown, TrendUp } from '@phosphor-icons/react';

interface ChartInsightsProps {
  datasets: Dataset[];
  chartData: any[];
  chartConfig: {
    type: string;
    xField?: string;
    yField?: string;
    categoryField?: string;
  };
}

export function ChartInsights({
  datasets,
  chartData,
  chartConfig,
}: ChartInsightsProps) {
  if (!chartData.length || !datasets.length) {
    return null;
  }

  const generateInsights = () => {
    const insights: string[] = [];

    if (chartConfig.type === 'pie' && chartData.length > 0) {
      const maxSegment = Math.max(...chartData.map(d => d.value));
      const totalCount = chartData.reduce((sum, d) => sum + d.value, 0);
      const maxPercentage = ((maxSegment / totalCount) * 100).toFixed(1);

      insights.push(
        `The largest category represents ${maxPercentage}% of the data`
      );

      if (chartData.length > 5) {
        insights.push(
          `Data is distributed across ${chartData.length} categories`
        );
      }

      // Check for dominance
      if (parseFloat(maxPercentage) > 50) {
        insights.push('One category dominates the distribution');
      } else if (parseFloat(maxPercentage) < 20) {
        insights.push('Data is relatively evenly distributed');
      }
    }

    if (chartConfig.type === 'histogram' && chartData.length > 0) {
      const maxFreq = Math.max(...chartData.map(d => d.count));
      const totalDataPoints = chartData.reduce((sum, d) => sum + d.count, 0);

      insights.push(`Peak frequency: ${maxFreq} observations`);
      insights.push(`Total sample size: ${totalDataPoints} data points`);

      // Check distribution shape
      const midIndex = Math.floor(chartData.length / 2);
      const leftSum = chartData
        .slice(0, midIndex)
        .reduce((sum, d) => sum + d.count, 0);
      const rightSum = chartData
        .slice(midIndex + 1)
        .reduce((sum, d) => sum + d.count, 0);

      if (Math.abs(leftSum - rightSum) / totalDataPoints < 0.1) {
        insights.push('Distribution appears roughly symmetric');
      } else if (leftSum > rightSum) {
        insights.push('Distribution is right-skewed');
      } else {
        insights.push('Distribution is left-skewed');
      }
    }

    if (
      (chartConfig.type === 'line' || chartConfig.type === 'bar') &&
      chartConfig.yField
    ) {
      const values = chartData.map(d => Number(d[chartConfig.yField!]) || 0);
      const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
      const variance =
        values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        values.length;
      const stdDev = Math.sqrt(variance);

      insights.push(`Average value: ${mean.toFixed(2)}`);
      insights.push(`Standard deviation: ${stdDev.toFixed(2)}`);

      // Trend analysis for line charts
      if (chartConfig.type === 'line' && values.length > 3) {
        const firstHalf = values.slice(0, Math.floor(values.length / 2));
        const secondHalf = values.slice(Math.floor(values.length / 2));
        const firstAvg =
          firstHalf.reduce((sum, val) => sum + val, 0) / firstHalf.length;
        const secondAvg =
          secondHalf.reduce((sum, val) => sum + val, 0) / secondHalf.length;

        const percentChange = ((secondAvg - firstAvg) / firstAvg) * 100;

        if (Math.abs(percentChange) > 10) {
          if (percentChange > 0) {
            insights.push(
              `Shows an upward trend (+${percentChange.toFixed(1)}%)`
            );
          } else {
            insights.push(
              `Shows a downward trend (${percentChange.toFixed(1)}%)`
            );
          }
        } else {
          insights.push('Shows relatively stable values');
        }
      }
    }

    if (
      chartConfig.type === 'scatter' &&
      chartConfig.xField &&
      chartConfig.yField
    ) {
      const xValues = chartData.map(d => Number(d[chartConfig.xField!]) || 0);
      const yValues = chartData.map(d => Number(d[chartConfig.yField!]) || 0);

      // Simple correlation calculation
      const n = xValues.length;
      const sumX = xValues.reduce((sum, val) => sum + val, 0);
      const sumY = yValues.reduce((sum, val) => sum + val, 0);
      const sumXY = xValues.reduce((sum, val, i) => sum + val * yValues[i], 0);
      const sumX2 = xValues.reduce((sum, val) => sum + val * val, 0);
      const sumY2 = yValues.reduce((sum, val) => sum + val * val, 0);

      const correlation =
        (n * sumXY - sumX * sumY) /
        Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

      if (!isNaN(correlation)) {
        const absCorr = Math.abs(correlation);

        if (absCorr > 0.7) {
          insights.push(
            `Strong ${correlation > 0 ? 'positive' : 'negative'} correlation (${correlation.toFixed(3)})`
          );
        } else if (absCorr > 0.3) {
          insights.push(
            `Moderate ${correlation > 0 ? 'positive' : 'negative'} correlation (${correlation.toFixed(3)})`
          );
        } else {
          insights.push(
            `Weak correlation between variables (${correlation.toFixed(3)})`
          );
        }
      }
    }

    return insights;
  };

  const insights = generateInsights();

  if (insights.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <Target className='w-5 h-5' />
          Automatic Insights
        </CardTitle>
        <CardDescription>
          AI-generated observations about your data visualization
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className='space-y-3'>
          {insights.map((insight, index) => (
            <div
              key={index}
              className='flex items-start gap-3 p-3 bg-muted/50 rounded-lg'
            >
              <div className='flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2' />
              <p className='text-sm leading-relaxed'>{insight}</p>
            </div>
          ))}
        </div>

        <div className='flex flex-wrap gap-2 mt-4'>
          <Badge variant='outline' className='text-xs'>
            Data Points: {chartData.length}
          </Badge>
          <Badge variant='outline' className='text-xs'>
            Chart Type: {chartConfig.type}
          </Badge>
          {chartConfig.xField && (
            <Badge variant='secondary' className='text-xs'>
              X: {chartConfig.xField}
            </Badge>
          )}
          {chartConfig.yField && (
            <Badge variant='secondary' className='text-xs'>
              Y: {chartConfig.yField}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
